import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Cards } from '@sorare/core/src/assets/icons/Cards.tsx';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { S27Title } from '@sorare/core/src/atoms/ui/S27Title/index.tsx';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { playableBlockchainRarities } from '@sorare/core/src/lib/cards.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { HexColor } from '@sorare/core/src/style/types.ts';
import { PillSwitchRarity } from '@sorare/marketplace/src/atoms/PillSwitchRarity/index.tsx';

import { CollectionMissions } from '#src/components/missions/CollectionMissions/index.tsx';
import { SetCollectionTaskDetails } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/SetCollectionTaskDetails/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import type { CollectionMissions_taskInterface } from '../CollectionMissions/__generated__/index.graphql.ts';
import type {
  CollectMissionsTaskDetailsQuery,
  CollectMissionsTaskDetailsQueryVariables,
} from './__generated__/index.graphql.ts';

const COLLECT_MISSIONS_TASK_DETAILS_QUERY = gql`
  query CollectMissionsTaskDetailsQuery($taskId: ID!) {
    currentUser {
      slug
      task(id: $taskId, bypassRebuildCollectionTask: true) {
        id
        ...SetCollectionTaskDetails_taskInterface
        ...getTaskCompetitionColor_taskInterface
      }
    }
  }
  ${SetCollectionTaskDetails.fragments.taskInterface}
  ${getTaskCompetitionColor.fragments.taskInterface}
` as TypedDocumentNode<
  CollectMissionsTaskDetailsQuery,
  CollectMissionsTaskDetailsQueryVariables
>;

const ContentWrapper = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  z-index: 1;
  overflow: hidden;
  max-height: 90vh;
  width: min(calc(80 * var(--vw)), 1000px);
`;

const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 424px 1fr;
  overflow: hidden;
`;

const LeftPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow-y: auto;
  padding: var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
`;

const RightPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden;
  flex: 1;
  min-width: 0;
  position: relative;
`;

const TaskDetailsWrapper = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--double-unit);
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  height: 40%;
  z-index: -1;
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(var(--c-rgb-black), 0) 0%,
      rgba(var(--c-rgb-black), 1) 50%
    );
  }
`;

type Props = {
  tasks: CollectionMissions_taskInterface[];
  open: boolean;
  onClose: () => void;
  rarity?: Rarity;
  onRarityChange?: (rarity: Rarity) => void;
  unclaimedTasksByRarity?: { [k in Rarity]?: number };
};

export const CollectMissionsDialog = ({
  tasks,
  open,
  onClose,
  rarity = Rarity.common,
  onRarityChange,
  unclaimedTasksByRarity,
}: Props) => {
  const setCollectionsTaskTracks = tasks;
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const availableCurrentSteps = setCollectionsTaskTracks
    .map(t => (isType(t, 'TasksTrack') ? t.currentStep?.id : null))
    .filter(Boolean);

  if (
    !selectedTaskId &&
    setCollectionsTaskTracks.length &&
    open &&
    availableCurrentSteps.length > 0
  ) {
    setSelectedTaskId(availableCurrentSteps.at(0) || null);
  }

  if (selectedTaskId && !availableCurrentSteps.includes(selectedTaskId)) {
    setSelectedTaskId(availableCurrentSteps[0]);
  }

  const { data: taskData } = useQuery(COLLECT_MISSIONS_TASK_DETAILS_QUERY, {
    variables: {
      taskId: idFromObject(selectedTaskId || ''),
    },
    skip: !selectedTaskId,
  });

  const task = taskData?.currentUser?.task;
  const taskSetColor = ((isType(task, 'CardsCountTargetTask')
    ? task.cardSet?.branding?.colors?.surfacePrimary
    : null) ?? 'var(--c-nd-600-opaque)') as HexColor;

  return (
    <Dialog open={open} onClose={onClose} hideHeader>
      <ContentWrapper>
        <TwoPanelLayout>
          <LeftPanel>
            <Vertical center gap={2} className="mb-3">
              <S27Title icon={<Cards size={3} />}>
                <FormattedMessage {...glossary.collect} />
              </S27Title>
              <BodyL className="text-center">
                <FormattedMessage defaultMessage="Hit collection milestones and get rewarded!" />
              </BodyL>
            </Vertical>
            <CollectionMissions
              tasks={setCollectionsTaskTracks}
              onTaskClick={taskId => {
                const track = setCollectionsTaskTracks.find(
                  t => t.id === taskId
                );
                const currentStep = isType(track, 'TasksTrack')
                  ? track.currentStep
                  : null;
                if (currentStep) {
                  setSelectedTaskId(currentStep.id);
                }
              }}
              selectedTaskId={selectedTaskId}
            />
            {!!onRarityChange && (
              <div className="sticky bottom-0 left-0 right-0 mt-auto">
                <PillSwitchRarity
                  rarities={playableBlockchainRarities.map(R => ({
                    value: R as Rarity,
                    badgeContent: unclaimedTasksByRarity?.[R as Rarity],
                  }))}
                  selectedRarity={rarity}
                  viewTransitionPrefix="rarity-picker"
                  onChange={onRarityChange}
                />
              </div>
            )}
          </LeftPanel>
          <RightPanel>
            <CloseButton onClose={onClose} fixed size="small" />
            {!!task && (
              <TaskDetailsWrapper>
                <StyledTextureBackground
                  background="wave2"
                  mode="light"
                  color={getTaskCompetitionColor(task, taskSetColor)}
                />
                <SetCollectionTaskDetails task={task} />
              </TaskDetailsWrapper>
            )}
          </RightPanel>
        </TwoPanelLayout>
      </ContentWrapper>
    </Dialog>
  );
};

CollectMissionsDialog.fragments = {
  taskInterface: CollectionMissions.fragments.taskInterface,
};
